import React from 'react';

const TwoTierAI = () => {
  return (
    <main className="container flow" style={{ minHeight: '100vh', paddingTop: '48px' }}>
      <h1>Two-Tier AI: How Companies Should Actually Use AI</h1>

      <p>
        Most companies are thinking about AI wrong. They either chase every new tool like it's magic, or they freeze up because they don't know where to start.
        The right way is simpler: two layers, working together.
      </p>

      <h2>Layer 1: General AI Enablement</h2>
      <p>
        Think of general AI like the office espresso machine. 
        It doesn't replace anyone. It just makes everyone sharper, faster, and more focused. 
        The difference is real, but it’s subtle, you don’t notice one coffee, but after a year the team gets a lot more done.
      </p>
      <p>
        Give people simple access to basic LLMs. Show them how to automate small tasks: writing drafts, summarising meetings, analysing first-pass data. 
        Not to show off, but to work better. Over time, continuous learning kicks in, and the baseline productivity of the whole company rises.
      </p>
      <p>
        Give employees the tools and freedom to self-direct. Don't guess what they need but let them pull what works. 
        This approach also spreads costs across the org, instead of locking you into expensive “layer 2” white-label AI products.
      </p>
      <p style={{ fontStyle: 'italic' }}>
        (Many "AI companies" resell the same foundation models. 
        True frontier AI—where a training run costs billions—is only built by a handful of players. 
        Be wary AI vendors who merely repackage frontier models.)
      </p>

      <h2>Layer 2: Targeted AI for the Business</h2>
      <p>
        Once you’ve raised the floor, you build specific tools for real problems. 
        Not off-the-shelf apps—solutions tailored to your work.
      </p>
      <p>
        If you review contracts, train a model on your contracts.  
        If you process claims, fine-tune on your cases.  
        If you build infrastructure, automate compliance checks and reporting.
      </p>
      <p>
        These targeted solutions can come from external companies and startups solving real niche gaps, or from in-house efforts defining key LLM personas and workflows. 
        It's an ROI exercise: rank opportunities by value, prioritise ruthlessly, and build a careful execution timeline that respects the real business.
      </p>
      <p>
        General AI makes everyone stronger. Targeted AI moves the needle for your core business.
      </p>

      <h2>Why It Works</h2>
      <p>
        Big companies fail at AI because they aim too high too fast. Start simple. Let everyone get stronger first. 
        Then build specific solutions where you feel the friction.
      </p>

      <h2>TL;DR</h2>
      <p>
        Layer 1: General AI to make everyone better.<br />
        Layer 2: Targeted AI to move your core business faster.<br />
        Give people the tools. Let demand pull. Rank value, move carefully. Keep it simple. Grow into it.
      </p>
    </main>
  );
};

export default TwoTierAI;
